import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";

// Components
import AdminHeader from "../components/admin/AdminHeader";
import AdminTabs from "../components/admin/AdminTabs";
import TradesTab from "../components/admin/TradesTab";
import UsersTab from "../components/admin/UsersTab";
import WithdrawalsTab from "../components/admin/WithdrawalsTab";
import ChatTab from "../components/admin/ChatTab";
import DepositsTab from "../components/admin/DepositsTab";
import NotificationsTab from "../components/admin/NotificationsTab";
import AdminSettingsTab from "../components/admin/AdminSettingsTab";
import PendingApprovalsTab from "../components/admin/PendingApprovalsTab";
import WalletsTab from "../components/admin/WalletsTab";
import AssetsTab from "../components/admin/AssetsTab";
import AutoTradingTab from "../components/admin/AutoTradingTab";
import AutoDepositsTab from "../components/admin/AutoDepositsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, token, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("approvals");
  const [trades, setTrades] = useState([]);
  const [users, setUsers] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Trade form
  const [showTradeForm, setShowTradeForm] = useState(false);
  const [tradeForm, setTradeForm] = useState({
    user_id: "",
    asset_name: "",
    investment: "",
    pnl_percent: "3.5",
    status: "active",
    close_date: "",
    scheduled_date: ""
  });
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedUserForTrade, setSelectedUserForTrade] = useState(null);

  // User edit
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ balance: "", trading_balance: "" });

  // PnL edit
  const [editingTradePnl, setEditingTradePnl] = useState(null);
  const [pnlForm, setPnlForm] = useState("");

  // Chat state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [unreadTotal, setUnreadTotal] = useState(0);

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [notifForm, setNotifForm] = useState({ title: "", message: "", user_id: "" });
  const [showNotifUserDropdown, setShowNotifUserDropdown] = useState(false);
  const [selectedUserForNotif, setSelectedUserForNotif] = useState(null);

  // Deposits state
  const [allDeposits, setAllDeposits] = useState([]);
  const [depositForm, setDepositForm] = useState({ user_id: "", amount: "", asset: "USDT", network: "TRC-20" });
  const [showDepositUserDropdown, setShowDepositUserDropdown] = useState(false);
  const [selectedUserForDeposit, setSelectedUserForDeposit] = useState(null);
  const [depositUserSearch, setDepositUserSearch] = useState("");

  // Reveal-hidden state (Вариант 1: global key gate)
  const [reveal, setReveal] = useState(!!sessionStorage.getItem("reveal_key"));

  useEffect(() => {
    const id = axios.interceptors.request.use((config) => {
      const key = sessionStorage.getItem("reveal_key");
      if (key) config.headers["X-Reveal-Key"] = key;
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, []);

  const handleToggleReveal = async () => {
    if (reveal) {
      sessionStorage.removeItem("reveal_key");
      setReveal(false);
      fetchData();
      return;
    }
    const key = window.prompt("Введите ключ доступа к скрытым клиентам:");
    if (!key) return;
    try {
      const res = await axios.get(`${API}/mnro/reveal/verify`, {
        headers: { Authorization: `Bearer ${token}`, "X-Reveal-Key": key }
      });
      if (res.data?.ok) {
        sessionStorage.setItem("reveal_key", key);
        setReveal(true);
        toast.success("Скрытые клиенты показаны");
        fetchData();
      } else {
        toast.error("Неверный ключ");
      }
    } catch (e) {
      toast.error("Неверный ключ");
    }
  };

  const handleToggleVisibility = async (userId, hidden) => {
    try {
      await axios.put(`${API}/mnro/users/${userId}/visibility`, { hidden }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(hidden ? "Клиент скрыт" : "Клиент виден");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка");
    }
  };

  useEffect(() => {
    fetchData();
    fetchUnreadTotal();
    
    // Refresh data every 5 seconds for live PnL
    const interval = setInterval(() => {
      fetchData();
      if (activeTab === "chat") {
        fetchConversations();
        if (selectedConversation) {
          fetchChatMessages(selectedConversation.user_id);
        }
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [token, activeTab, selectedConversation]);

  useEffect(() => {
    if (activeTab === "chat") {
      fetchConversations();
    }
    if (activeTab === "notifications") {
      fetchNotifications();
    }
    if (activeTab === "deposits") {
      fetchDeposits();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const [tradesRes, usersRes, withdrawalsRes, pendingRes] = await Promise.all([
        axios.get(`${API}/mnro/trades`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/mnro/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/mnro/withdrawals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/mnro/users/pending-approval`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTrades(tradesRes.data);
      setUsers(usersRes.data);
      setWithdrawals(withdrawalsRes.data);
      setPendingApprovals(pendingRes.data.length);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Ошибка загрузки данных");
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/mnro/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const fetchDeposits = async () => {
    try {
      const res = await axios.get(`${API}/mnro/deposits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllDeposits(res.data);
    } catch (error) {
      console.error("Error fetching deposits:", error);
    }
  };

  const fetchUnreadTotal = async () => {
    try {
      const res = await axios.get(`${API}/mnro/chat/unread-total`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadTotal(res.data.count);
    } catch (error) {
      console.error("Error fetching unread:", error);
    }
  };

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/mnro/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  };

  const fetchChatMessages = async (userId) => {
    try {
      const res = await axios.get(`${API}/mnro/chat/messages/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatMessages(res.data);
      fetchUnreadTotal();
      fetchConversations();
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Получить только обычных пользователей (не админов)
  const regularUsers = users.filter(u => !u.is_admin);

  // Trade functions
  const handleCreateTrade = async (e) => {
    e.preventDefault();
    if (!tradeForm.user_id) {
      toast.error("Выберите пользователя");
      return;
    }
    try {
      await axios.post(`${API}/mnro/trades`, {
        user_id: tradeForm.user_id,
        asset_name: tradeForm.asset_name,
        investment: parseFloat(tradeForm.investment),
        pnl_percent: parseFloat(tradeForm.pnl_percent),
        status: tradeForm.status,
        close_date: tradeForm.close_date || null,
        scheduled_date: tradeForm.scheduled_date || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(tradeForm.status === "scheduled" ? "Отложенная сделка создана" : "Сделка создана");
      setShowTradeForm(false);
      setTradeForm({ user_id: "", asset_name: "", investment: "", pnl_percent: "3.5", status: "active", close_date: "", scheduled_date: "" });
      setSelectedUserForTrade(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка создания сделки");
    }
  };

  const handleDeleteTrade = async (tradeId) => {
    try {
      await axios.delete(`${API}/mnro/trades/${tradeId}?confirm=true`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Сделка удалена");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка удаления");
      throw error;
    }
  };

  const handleCloseTrade = async (tradeId) => {
    try {
      await axios.put(`${API}/mnro/trades/${tradeId}`, {
        status: "closed",
        close_date: new Date().toISOString()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Сделка закрыта");
      fetchData();
    } catch (error) {
      toast.error("Ошибка закрытия сделки");
    }
  };

  const handleUpdatePnl = async (tradeId) => {
    try {
      await axios.put(`${API}/mnro/trades/${tradeId}`, {
        current_pnl_percent: parseFloat(pnlForm)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("PnL обновлён");
      setEditingTradePnl(null);
      setPnlForm("");
      fetchData();
    } catch (error) {
      toast.error("Ошибка обновления PnL");
    }
  };

  // User functions
  const handleUpdateUser = async (userId) => {
    try {
      const updateData = {};
      if (userForm.balance !== "") {
        updateData.balance = parseFloat(userForm.balance);
      }
      if (userForm.trading_balance !== "") {
        updateData.trading_balance = parseFloat(userForm.trading_balance);
      }
      
      await axios.put(`${API}/mnro/users/${userId}`, updateData, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success("Баланс обновлён");
      setEditingUser(null);
      setUserForm({ balance: "", trading_balance: "" });
      fetchData();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Ошибка обновления");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/mnro/users/${userId}?confirm=true`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Пользователь и все связанные данные удалены");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка удаления");
    }
  };

  // Topup user balance
  const handleTopupUser = async (userId, amount) => {
    try {
      const response = await axios.post(`${API}/mnro/users/${userId}/topup`, {
        user_id: userId,
        amount: amount
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || `Баланс пополнен на $${amount}`);
      fetchData();
    } catch (error) {
      console.error("Topup error:", error);
      toast.error(error.response?.data?.detail || "Ошибка пополнения");
      throw error;
    }
  };

  // Withdrawal functions
  const handleWithdrawalStatus = async (withdrawalId, status) => {
    try {
      await axios.put(`${API}/mnro/withdrawals/${withdrawalId}?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Статус обновлён: ${status === "completed" ? "Выполнено" : "Отклонено"}`);
      fetchData();
    } catch (error) {
      toast.error("Ошибка обновления статуса");
    }
  };

  // Chat functions
  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    fetchChatMessages(conv.user_id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      await axios.post(`${API}/mnro/chat/messages/${selectedConversation.user_id}`,
        { message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage("");
      fetchChatMessages(selectedConversation.user_id);
      fetchConversations();
    } catch (error) {
      toast.error("Ошибка отправки сообщения");
    }
  };

  // Deposit functions
  const handleCreateDeposit = async (e) => {
    e.preventDefault();
    if (!depositForm.user_id || !depositForm.amount) {
      toast.error("Выберите пользователя и укажите сумму");
      return;
    }
    try {
      await axios.post(`${API}/mnro/deposits`, {
        user_id: depositForm.user_id,
        amount: parseFloat(depositForm.amount),
        asset: depositForm.asset,
        network: depositForm.network
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Депозит зачислен");
      setDepositForm({ user_id: "", amount: "", asset: "USDT", network: "TRC-20" });
      setSelectedUserForDeposit(null);
      setDepositUserSearch("");
      fetchDeposits();
      fetchData();
    } catch (error) {
      toast.error("Ошибка зачисления депозита");
    }
  };

  // Notification functions
  const handleCreateNotification = async (e) => {
    e.preventDefault();
    if (!notifForm.title.trim() || !notifForm.message.trim()) {
      toast.error("Заполните заголовок и текст");
      return;
    }
    try {
      await axios.post(`${API}/mnro/notifications`, {
        title: notifForm.title,
        message: notifForm.message,
        user_id: notifForm.user_id || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Уведомление отправлено");
      setNotifForm({ title: "", message: "", user_id: "" });
      setSelectedUserForNotif(null);
      fetchNotifications();
    } catch (error) {
      toast.error("Ошибка отправки уведомления");
    }
  };

  const handleDeleteNotification = async (notifId) => {
    if (!window.confirm("Удалить уведомление?")) return;
    try {
      await axios.delete(`${API}/mnro/notifications/${notifId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Уведомление удалено");
      fetchNotifications();
    } catch (error) {
      toast.error("Ошибка удаления");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bybit-grid-subtle flex items-center justify-center">
        <div className="text-crypto-orange">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bybit-grid-subtle">
      <AdminHeader user={user} token={token} onLogout={handleLogout} reveal={reveal} onToggleReveal={handleToggleReveal} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AdminTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tradesCount={trades.length}
          usersCount={regularUsers.length}
          pendingWithdrawals={withdrawals.filter(w => w.status === "pending").length}
          unreadTotal={unreadTotal}
          pendingApprovals={pendingApprovals}
        />

        {activeTab === "approvals" && (
          <PendingApprovalsTab
            token={token}
            onApproved={fetchData}
          />
        )}

        {activeTab === "trades" && (
          <TradesTab
            trades={trades}
            regularUsers={regularUsers}
            showTradeForm={showTradeForm}
            setShowTradeForm={setShowTradeForm}
            tradeForm={tradeForm}
            setTradeForm={setTradeForm}
            showUserDropdown={showUserDropdown}
            setShowUserDropdown={setShowUserDropdown}
            selectedUserForTrade={selectedUserForTrade}
            setSelectedUserForTrade={setSelectedUserForTrade}
            editingTradePnl={editingTradePnl}
            setEditingTradePnl={setEditingTradePnl}
            pnlForm={pnlForm}
            setPnlForm={setPnlForm}
            onCreateTrade={handleCreateTrade}
            onDeleteTrade={handleDeleteTrade}
            onCloseTrade={handleCloseTrade}
            onUpdatePnl={handleUpdatePnl}
            onUpdate={fetchData}
            token={token}
          />
        )}

        {activeTab === "users" && (
          <UsersTab
            regularUsers={regularUsers}
            trades={trades}
            withdrawals={withdrawals}
            deposits={allDeposits}
            token={token}
            reveal={reveal}
            onToggleVisibility={handleToggleVisibility}
            onDeleteUser={handleDeleteUser}
            onUpdate={fetchData}
          />
        )}

        {activeTab === "withdrawals" && (
          <WithdrawalsTab
            withdrawals={withdrawals}
            onWithdrawalStatus={handleWithdrawalStatus}
          />
        )}

        {activeTab === "chat" && (
          <ChatTab
            conversations={conversations}
            selectedConversation={selectedConversation}
            chatMessages={chatMessages}
            newMessage={newMessage}
            setNewMessage={setNewMessage}
            onSelectConversation={handleSelectConversation}
            onSendMessage={handleSendMessage}
            onRefresh={fetchChatMessages}
            token={token}
            regularUsers={regularUsers}
          />
        )}

        {activeTab === "deposits" && (
          <DepositsTab
            allDeposits={allDeposits}
            regularUsers={regularUsers}
            depositForm={depositForm}
            setDepositForm={setDepositForm}
            showDepositUserDropdown={showDepositUserDropdown}
            setShowDepositUserDropdown={setShowDepositUserDropdown}
            selectedUserForDeposit={selectedUserForDeposit}
            setSelectedUserForDeposit={setSelectedUserForDeposit}
            depositUserSearch={depositUserSearch}
            setDepositUserSearch={setDepositUserSearch}
            onCreateDeposit={handleCreateDeposit}
          />
        )}

        {activeTab === "wallets" && (
          <WalletsTab
            regularUsers={regularUsers}
            onUserUpdated={fetchData}
          />
        )}

        {activeTab === "assets" && (
          <AssetsTab token={token} />
        )}

        {activeTab === "autotrading" && (
          <AutoTradingTab token={token} />
        )}

        {activeTab === "autodeposits" && (
          <AutoDepositsTab token={token} />
        )}

        {activeTab === "notifications" && (
          <NotificationsTab
            notifications={notifications}
            regularUsers={regularUsers}
            notifForm={notifForm}
            setNotifForm={setNotifForm}
            showNotifUserDropdown={showNotifUserDropdown}
            setShowNotifUserDropdown={setShowNotifUserDropdown}
            selectedUserForNotif={selectedUserForNotif}
            setSelectedUserForNotif={setSelectedUserForNotif}
            onCreateNotification={handleCreateNotification}
            onDeleteNotification={handleDeleteNotification}
          />
        )}

        {activeTab === "settings" && (
          <AdminSettingsTab
            user={user}
            token={token}
            onUserUpdate={refreshUser}
          />
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
